import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { fmtDate } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Star, AlertTriangle, ExternalLink, Mail } from "lucide-react";
import DraftEmailDialog from "@/components/consultants/DraftEmailDialog";
import { TEMPLATES, firstName, subjectFor, buildGmailUrl } from "@/lib/consultantEmail";

const STATUS_META = {
  needed: { label: "Needed", bg: "#F1F1EF", color: "#52525B" },
  quote_requested: { label: "Quote requested", bg: "#FEF3C7", color: "#92400E" },
  quote_received: { label: "Quote received", bg: "#DBEAFE", color: "#1E40AF" },
  engaged: { label: "Quote accepted", bg: "#E0E7FF", color: "#3730A3" },
  report_received: { label: "Report received", bg: "#D1FAE5", color: "#065F46" },
  invoice_paid: { label: "Invoice paid", bg: "#DCFCE7", color: "#166534" },
  not_required: { label: "Not required", bg: "#F4F4F5", color: "#A1A1AA" },
};
const STATUS_ORDER = ["needed", "quote_requested", "quote_received", "engaged", "report_received", "invoice_paid", "not_required"];

export default function ProjectConsultants({ projectId, project, members = [], canManage }) {
  const [meta, setMeta] = useState({ disciplines: [], statuses: [] });
  const [directory, setDirectory] = useState([]);
  const [data, setData] = useState({ rows: [], summary: {}, overdue_count: 0 });
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [newService, setNewService] = useState("");
  const [emailRow, setEmailRow] = useState(null);

  const memberName = useMemo(() => Object.fromEntries(members.map((m) => [m.user_id, m.name])), [members]);
  const memberEmail = useMemo(() => Object.fromEntries(members.map((m) => [m.user_id, m.email])), [members]);
  const dirById = useMemo(() => Object.fromEntries(directory.map((c) => [c.consultant_id, c])), [directory]);

  useEffect(() => {
    api.get("/consultants/meta").then((r) => setMeta(r.data));
    api.get("/consultants").then((r) => setDirectory(r.data));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    const r = await api.get(`/projects/${projectId}/consultants`);
    setData(r.data); setLoading(false);
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  const patch = async (rowId, body) => {
    try {
      const r = await api.patch(`/projects/${projectId}/consultants/${rowId}`, body);
      setData((d) => ({ ...d, rows: d.rows.map((x) => x.row_id === rowId ? r.data : x) }));
      // reload for summary + overdue + task side effects
      load();
    } catch (e) { toast.error(apiErr(e, "Could not update")); }
  };

  const STATUS_EMAIL = { quote_requested: "request_quote", engaged: "accept_quote", invoice_paid: "invoice_paid" };

  const openEmail = (row, templateKey) => {
    const consultant = dirById[row.consultant_id];
    const to = (consultant?.emails || []).join(", ");
    const tpl = TEMPLATES[templateKey];
    if (!row.consultant_id || !to || !tpl) {
      toast.message("Add a consultant with an email to draft this message.");
      return;
    }
    const fname = firstName(consultant?.contacts, consultant?.company);
    const cc = memberEmail[row.assigned_to] || "";
    const url = buildGmailUrl({ to, cc, subject: subjectFor(project, row.service), body: tpl.body(fname, row.service) });
    window.open(url, "_blank");
    api.post(`/projects/${projectId}/consultants/${row.row_id}/email-drafted`, { mode: tpl.mode, template: tpl.label }).catch(() => {});
  };

  const handleStatus = (row, v) => {
    const key = STATUS_EMAIL[v];
    if (key && v !== row.status) openEmail(row, key);
    patch(row.row_id, { status: v });
  };

  const addRow = async () => {
    if (!newService) { toast.error("Pick a service"); return; }
    setAdding(true);
    try {
      await api.post(`/projects/${projectId}/consultants`, { service: newService, status: "needed" });
      setNewService(""); load();
    } catch (e) { toast.error(apiErr(e, "Could not add")); }
    finally { setAdding(false); }
  };

  const removeRow = async (rowId) => {
    try { await api.delete(`/projects/${projectId}/consultants/${rowId}`); load(); }
    catch { toast.error("Could not remove"); }
  };

  const consultantsFor = (service) => {
    const list = directory.filter((c) => (c.disciplines || []).includes(service));
    return list.sort((a, b) => (a.preferred === "preferred" ? -1 : 0) - (b.preferred === "preferred" ? -1 : 0)
      || a.company.localeCompare(b.company));
  };

  const chips = STATUS_ORDER.map((s) => ({ s, n: data.summary[s] || 0 })).filter((c) => c.n > 0);

  return (
    <div data-testid="project-consultants">
      {/* Summary chips */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {chips.length === 0 && <span className="text-sm text-zinc-400">No consultants yet.</span>}
        {chips.map(({ s, n }) => (
          <span key={s} className="text-xs font-semibold px-2.5 py-1 rounded-full" style={{ background: STATUS_META[s].bg, color: STATUS_META[s].color }} data-testid={`chip-${s}`}>
            {n} {STATUS_META[s].label.toLowerCase()}
          </span>
        ))}
        {data.overdue_count > 0 && (
          <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-rose-100 text-rose-700" data-testid="chip-overdue">
            <AlertTriangle className="h-3 w-3" /> {data.overdue_count} overdue
          </span>
        )}
      </div>

      {loading ? (
        <div className="h-7 w-7 border-2 border-zinc-900 border-t-transparent rounded-full animate-spin" />
      ) : (
        <div className="space-y-3">
          {data.rows.map((row) => {
            const sm = STATUS_META[row.status] || STATUS_META.needed;
            const options = consultantsFor(row.service);
            return (
              <div key={row.row_id} data-testid={`consultant-row-${row.row_id}`}
                className={`bg-white border rounded-sm p-3 ${row.overdue ? "border-rose-300" : "border-zinc-200"}`}>
                <div className="grid lg:grid-cols-[180px_1fr_150px] gap-3">
                  {/* Service */}
                  <div className="min-w-0">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400">Service</p>
                    <p className="text-sm font-semibold mt-0.5 truncate flex items-center gap-1">
                      {row.service}
                      {row.flagged && <AlertTriangle className="h-3.5 w-3.5 text-amber-500" title="Condition changed — review" />}
                    </p>
                    {row.overdue && <span className="text-[10px] text-rose-600 font-semibold">Overdue — no change &gt;5 working days</span>}
                  </div>
                  {/* Consultant picker */}
                  <div className="min-w-0">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400">Consultant</p>
                    <Select value={row.consultant_id || "none"} onValueChange={(v) => patch(row.row_id, { consultant_id: v === "none" ? null : v })} disabled={!canManage}>
                      <SelectTrigger className="mt-0.5 h-8 rounded-sm text-sm" data-testid={`row-consultant-${row.row_id}`}><SelectValue placeholder="Not yet appointed" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Not yet appointed</SelectItem>
                        {options.map((c) => (
                          <SelectItem key={c.consultant_id} value={c.consultant_id}>
                            {c.preferred === "preferred" ? "⭐ " : ""}{c.company}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {/* Status */}
                  <div className="min-w-0">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400">Status</p>
                    <Select value={row.status} onValueChange={(v) => handleStatus(row, v)} disabled={!canManage}>
                      <SelectTrigger className="mt-0.5 h-8 rounded-sm text-sm font-semibold" data-testid={`row-status-${row.row_id}`}
                        style={{ background: sm.bg, color: sm.color }}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {STATUS_ORDER.map((s) => <SelectItem key={s} value={s}>{STATUS_META[s].label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-3">
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400">Quote $</p>
                    <Input type="number" defaultValue={row.quote_amount ?? ""} disabled={!canManage}
                      onBlur={(e) => { const v = e.target.value === "" ? null : Number(e.target.value); if (v !== (row.quote_amount ?? null)) patch(row.row_id, { quote_amount: v }); }}
                      className="mt-0.5 h-8 rounded-sm text-sm" data-testid={`row-quote-${row.row_id}`} />
                  </div>
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400">Assigned to</p>
                    <Select value={row.assigned_to || "none"} onValueChange={(v) => patch(row.row_id, { assigned_to: v === "none" ? null : v })} disabled={!canManage}>
                      <SelectTrigger className="mt-0.5 h-8 rounded-sm text-sm" data-testid={`row-assignee-${row.row_id}`}><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Unassigned</SelectItem>
                        {members.map((m) => <SelectItem key={m.user_id} value={m.user_id}>{m.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="sm:col-span-2">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400">Report / file link</p>
                    <div className="flex items-center gap-1.5">
                      <Input defaultValue={row.report_link || ""} disabled={!canManage} placeholder="https://drive.google.com/…"
                        onBlur={(e) => { if (e.target.value.trim() !== (row.report_link || "")) patch(row.row_id, { report_link: e.target.value.trim() }); }}
                        className="mt-0.5 h-8 rounded-sm text-sm" data-testid={`row-link-${row.row_id}`} />
                      {row.report_link && <a href={row.report_link} target="_blank" rel="noreferrer" className="mt-0.5 text-zinc-400 hover:text-zinc-900"><ExternalLink className="h-4 w-4" /></a>}
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between gap-3 mt-2">
                  <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-[11px] text-zinc-500">
                    {row.date_requested && <span>Requested {fmtDate(row.date_requested)}</span>}
                    {row.date_engaged && <span>Accepted {fmtDate(row.date_engaged)}</span>}
                    {row.date_report_received && <span>Report {fmtDate(row.date_report_received)}</span>}
                    {row.consultant_name && <span className="inline-flex items-center gap-1">{row.consultant_emails?.length > 0 ? row.consultant_emails[0] : ""}</span>}
                  </div>
                  {canManage && (
                    <div className="flex items-center gap-2 shrink-0">
                      {row.overdue && (
                        <button onClick={() => openEmail(row, "chase")} data-testid={`row-chase-${row.row_id}`}
                          disabled={!row.consultant_id}
                          className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 hover:text-amber-900 disabled:opacity-40 transition-colors" title="Draft a chase email">
                          <AlertTriangle className="h-3.5 w-3.5" /> Chase
                        </button>
                      )}
                      <button onClick={() => setEmailRow(row)} data-testid={`row-email-${row.row_id}`}
                        disabled={!row.consultant_id}
                        className="inline-flex items-center gap-1 text-xs font-medium text-zinc-500 hover:text-zinc-900 disabled:opacity-40 transition-colors" title={row.consultant_id ? "Draft email" : "Pick a consultant first"}>
                        <Mail className="h-3.5 w-3.5" /> Draft email
                      </button>
                      <button onClick={() => removeRow(row.row_id)} data-testid={`row-delete-${row.row_id}`}
                        className="text-zinc-300 hover:text-rose-600 transition-colors"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {canManage && (
            <div className="flex items-center gap-2 bg-white border border-dashed border-zinc-300 rounded-sm p-3">
              <Select value={newService} onValueChange={setNewService}>
                <SelectTrigger className="h-9 rounded-sm text-sm max-w-xs" data-testid="new-consultant-service"><SelectValue placeholder="Add a service…" /></SelectTrigger>
                <SelectContent>
                  {meta.disciplines.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button onClick={addRow} disabled={adding} className="bg-zinc-900 hover:bg-zinc-800 rounded-sm gap-1.5 h-9" data-testid="add-consultant-row">
                <Plus className="h-4 w-4" /> Add row
              </Button>
            </div>
          )}
        </div>
      )}

      <DraftEmailDialog open={!!emailRow} onOpenChange={(o) => !o && setEmailRow(null)}
        projectId={projectId} project={project} service={emailRow?.service}
        consultant={emailRow ? { ...dirById[emailRow.consultant_id], row_id: emailRow.row_id } : null}
        assignedEmail={emailRow ? memberEmail[emailRow.assigned_to] : ""}
        onSent={load} />
    </div>
  );
}
